import {
  STORAGE_LOCATIONS,
  STORAGE_LOCATION_LABELS,
  UNIT_KEYS,
  UNITS,
  type StorageLocation,
} from "@sebas/core";
import { Input, NativeSelect, Select } from "@/components/ui/Field";
import { cn } from "@/lib/cn";

/** A quantity and unit as the form holds them — strings until they are saved. */
export type AmountDraft = { quantity: string; unit: string };

/** The bare-count unit is the empty string; it needs a word in a menu. */
export const UNIT_OPTIONS = UNIT_KEYS.map((u) => ({
  value: u,
  label: u === "" ? "count" : UNITS[u].label,
}));

/**
 * A pared-down unit list for the fridge, where people track whole groceries
 * rather than recipe measures: a bare count plus the common weight/volume units.
 * Values stay canonical so amounts still convert against recipe ingredients.
 */
export const FRIDGE_UNIT_OPTIONS = [
  { value: "", label: "qty" },
  { value: "kg", label: "kg" },
  { value: "g", label: "gr" },
  { value: "ml", label: "ml" },
  { value: "l", label: "liter" },
];

export const LOCATION_OPTIONS = STORAGE_LOCATIONS.map((l) => ({
  value: l,
  label: STORAGE_LOCATION_LABELS[l],
}));

/** Blank means "some, amount unknown" — a real state, not a validation failure. */
export const parseQuantity = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) && n >= 0 ? n : null;
};

export function QuantityFields({
  draft,
  onChange,
  size = "sm",
  unitOptions = UNIT_OPTIONS,
}: {
  draft: AmountDraft;
  onChange: (next: AmountDraft) => void;
  size?: "sm" | "md";
  unitOptions?: { value: string; label: string }[];
}) {
  return (
    <>
      <Input
        className="w-20 shrink-0"
        size={size}
        type="number"
        min="0"
        step="any"
        inputMode="decimal"
        placeholder="Qty"
        aria-label="Quantity"
        value={draft.quantity}
        onChange={(e) => onChange({ ...draft, quantity: e.target.value })}
      />
      <NativeSelect
        className="w-24 shrink-0"
        size={size}
        aria-label="Unit"
        value={draft.unit}
        options={unitOptions}
        onChange={(e) => onChange({ ...draft, unit: e.target.value })}
      />
    </>
  );
}

export function LocationSelect({
  value,
  onChange,
  size = "sm",
}: {
  value: StorageLocation;
  onChange: (value: StorageLocation) => void;
  size?: "sm" | "md";
}) {
  return (
    <Select
      className="w-28 shrink-0"
      size={size}
      aria-label="Where it lives"
      value={value}
      options={LOCATION_OPTIONS}
      onChange={(v) => onChange(v as StorageLocation)}
    />
  );
}

/**
 * Location as a segmented control — three taps, all visible at once.
 * Beats a dropdown on a phone, where every extra tap-then-scroll costs.
 */
export function LocationSegmented({
  value,
  onChange,
  className,
}: {
  value: StorageLocation;
  onChange: (value: StorageLocation) => void;
  className?: string;
}) {
  return (
    <div
      role="radiogroup"
      aria-label="Where it lives"
      className={cn("inline-flex rounded-full bg-sunken p-0.5", className)}
    >
      {STORAGE_LOCATIONS.map((loc) => {
        const active = value === loc;
        return (
          <button
            key={loc}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(loc)}
            className={cn(
              "flex-1 rounded-full px-3 py-1.5 text-xs font-medium transition",
              active ? "bg-raised text-ink shadow-card" : "text-ink-faint hover:text-ink",
            )}
          >
            {STORAGE_LOCATION_LABELS[loc]}
          </button>
        );
      })}
    </div>
  );
}
